import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { FormInput } from "@/components/shared/FormInput";
import { PrimaryButton } from "@/components/shared/Buttons";
import { submitEnquiry, MODES, QUALIFICATIONS, isValidIndianMobile, isValidEmail } from "@/lib/api";
import { PROGRAMS_DATA } from "@/lib/programsData";

const COURSE_OPTIONS = [...PROGRAMS_DATA.map((p) => p.name), "Not sure yet"];

const SelectField = ({ label, id, value, onChange, options, error, onDark, placeholder, testId }) => {
  const cls = `w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors ${
    onDark
      ? "border-white/15 bg-white/5 text-white focus:border-orange"
      : "border-navy/15 bg-white text-navy focus:border-royal"
  } ${error ? "border-destructive" : ""}`;
  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label htmlFor={id} className={`text-xs font-semibold uppercase tracking-wider ${onDark ? "text-white/70" : "text-navy/70"}`}>{label}</label>
      )}
      <select id={id} value={value} onChange={onChange} data-testid={testId} className={cls}>
        <option value="">{placeholder || "Select"}</option>
        {options.map((o) => (
          <option key={o} value={o} className="text-navy">{o}</option>
        ))}
      </select>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
};

const initial = {
  fullName: "", mobile: "", email: "", qualification: "", passingYear: "",
  college: "", courseInterested: "", preferredMode: "", message: "",
};

export const EnquiryForm = ({ onDark = false, source = "website", defaultCourse = "", testId = "enquiry-form" }) => {
  const [form, setForm] = useState({ ...initial, courseInterested: defaultCourse });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Full name is required.";
    if (!isValidIndianMobile(form.mobile)) e.mobile = "Enter a valid 10-digit Indian mobile number.";
    if (!isValidEmail(form.email)) e.email = "Enter a valid email address.";
    if (!form.qualification) e.qualification = "Qualification is required.";
    if (!form.passingYear.trim()) e.passingYear = "Passing year is required.";
    if (!form.college.trim()) e.college = "College / University is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await submitEnquiry({ ...form, source });
      toast.success(res.message || "Enquiry submitted!");
      setDone(true);
    } catch (err) {
      setErrors(err.errors || {});
      toast.error(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testId}-success`} className={`flex flex-col items-center gap-4 rounded-2xl border p-10 text-center ${onDark ? "border-white/10 bg-white/[0.03]" : "border-navy/10 bg-white"}`}>
        <CheckCircle2 className="h-12 w-12 text-orange" />
        <h3 className={`font-heading text-2xl ${onDark ? "text-white" : "text-navy"}`}>Thank you!</h3>
        <p className={`text-sm ${onDark ? "text-white/70" : "text-navy/70"}`}>Our team will contact you shortly to help with your enquiry.</p>
      </div>
    );
  }

  const wrap = onDark ? "border-white/10 bg-white/[0.03]" : "border-navy/10 bg-white";
  return (
    <form onSubmit={onSubmit} noValidate data-testid={testId} className={`flex flex-col gap-4 rounded-2xl border p-6 sm:p-8 ${wrap}`}>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormInput label="Full Name" id="enq-name" value={form.fullName} onChange={set("fullName")} error={errors.fullName} onDark={onDark} placeholder="Your name" testId={`${testId}-fullName`} />
        <FormInput label="Mobile Number" id="enq-mobile" value={form.mobile} onChange={set("mobile")} error={errors.mobile} onDark={onDark} placeholder="10-digit mobile" testId={`${testId}-mobile`} />
        <FormInput label="Email Address" id="enq-email" type="email" value={form.email} onChange={set("email")} error={errors.email} onDark={onDark} placeholder="you@example.com" testId={`${testId}-email`} />
        <SelectField label="Qualification" id="enq-qual" value={form.qualification} onChange={set("qualification")} options={QUALIFICATIONS} error={errors.qualification} onDark={onDark} placeholder="Select qualification" testId={`${testId}-qualification`} />
        <FormInput label="Passing Year" id="enq-year" value={form.passingYear} onChange={set("passingYear")} error={errors.passingYear} onDark={onDark} placeholder="e.g. 2025" testId={`${testId}-passingYear`} />
        <FormInput label="College / University" id="enq-college" value={form.college} onChange={set("college")} error={errors.college} onDark={onDark} placeholder="Institution name" testId={`${testId}-college`} />
        <SelectField label="Course Interested In" id="enq-course" value={form.courseInterested} onChange={set("courseInterested")} options={COURSE_OPTIONS} onDark={onDark} placeholder="Select a course" testId={`${testId}-course`} />
        <SelectField label="Preferred Mode" id="enq-mode" value={form.preferredMode} onChange={set("preferredMode")} options={MODES} onDark={onDark} placeholder="Select mode" testId={`${testId}-mode`} />
      </div>
      <FormInput label="Message (optional)" id="enq-msg" as="textarea" value={form.message} onChange={set("message")} onDark={onDark} placeholder="Anything you'd like us to know?" testId={`${testId}-message`} />
      <PrimaryButton type="submit" size="lg" disabled={submitting} testId={`${testId}-submit`}>
        {submitting ? "Sending…" : "Request a Callback"}
      </PrimaryButton>
    </form>
  );
};
